import type { FirebaseOptions } from 'firebase/app';
import type { Auth } from 'firebase/auth';
import head from 'lodash/head';

// SIP is single-tenant per instance, so a single SAML provider id is injected at
// deploy time (window.SSO_PROVIDER_ID), unlike STM's multi-tenant domain map.
// The Firebase client config is injected the same way STM injects it
// (window.FIREBASE_CONFIG). Both are absent for saml2-mode / non-SSO instances,
// in which case firebase auth stays disabled and this module is inert.
//
// Sign-in uses the REDIRECT flow, not a popup: the whole tab navigates to the
// Firebase auth handler and back. A popup would be blocked whenever it is not
// opened straight from a click, and the auth handler is a same-site subdomain
// (auth-<env>.skillmore.com next to sip-<env>.skillmore.com), so its storage is
// not partitioned away from the app's.
//
// The firebase SDK is imported lazily (dynamic import) so that merely importing
// this module (e.g. from the global context) never pulls the firebase runtime.
// That keeps saml2 instances and the jest test suite free of the firebase ESM.

const parseInjected = (raw?: string | null): string => {
  // Decode HTML entities from server-side template injection (matches STM).
  try {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = raw || '';
    return textArea.value;
  } catch (_error) {
    return raw || '';
  }
};

let firebaseConfig: FirebaseOptions | null = null;
try {
  const parsed = JSON.parse(parseInjected(window.FIREBASE_CONFIG) || '{}');
  if (parsed && parsed.apiKey && parsed.projectId) firebaseConfig = parsed;
} catch (_error) {
  firebaseConfig = null;
}

export const SSO_PROVIDER_ID = parseInjected(window.SSO_PROVIDER_ID) || '';

export const isFirebaseAuthEnabled = (): boolean => Boolean(firebaseConfig && SSO_PROVIDER_ID);

// Promise-singleton so concurrent callers share one init and we never reassign
// module state across an await (avoids require-atomic-updates races).
let authPromise: Promise<Auth | null> | null = null;

const getAuthInstance = (): Promise<Auth | null> => {
  const config = firebaseConfig;
  if (!config) return Promise.resolve(null);
  if (!authPromise) {
    authPromise = (async () => {
      const { getApps, initializeApp } = await import('firebase/app');
      const { getAuth } = await import('firebase/auth');
      const app = head(getApps()) || initializeApp(config);
      return getAuth(app);
    })();
  }
  return authPromise;
};

// Leave the app for the Firebase SAML handler. Resolves only if the navigation
// could not be started — on success the tab is already gone.
export const startFirebaseSamlLogin = async (): Promise<void> => {
  const authInstance = await getAuthInstance();
  if (!authInstance || !SSO_PROVIDER_ID) throw new Error('Firebase auth is not configured');
  const { signInWithRedirect, SAMLAuthProvider } = await import('firebase/auth');
  await signInWithRedirect(authInstance, new SAMLAuthProvider(SSO_PROVIDER_ID));
};

// Promise-singleton: getRedirectResult() reports the credential exactly once per
// page load, so every caller must share the same call or the second one sees null.
let idTokenPromise: Promise<string | null> | null = null;

// The Firebase ID token (a JWT) for this page load, from the SAML redirect we
// just came back from or from a session Firebase already persists. Null when
// nobody is signed in — the caller should then start a redirect.
export const getFirebaseIdToken = (): Promise<string | null> => {
  if (!idTokenPromise) {
    idTokenPromise = (async () => {
      const authInstance = await getAuthInstance();
      if (!authInstance) return null;
      const { getRedirectResult } = await import('firebase/auth');
      // Also settles auth's persistence restore, so currentUser is meaningful after it.
      const result = await getRedirectResult(authInstance);
      const user = result?.user || authInstance.currentUser;
      return user ? user.getIdToken() : null;
    })();
  }
  return idTokenPromise;
};

export const firebaseSignOut = async (): Promise<void> => {
  const authInstance = await getAuthInstance();
  if (authInstance) {
    const { signOut } = await import('firebase/auth');
    await signOut(authInstance);
  }
};
